/**
 * AG Grid Cell Renderers for PACS Table
 * These renderers match the existing Ant Design table styling
 */

import React from 'react';
import { ICellRendererParams } from 'ag-grid-community';
import { Tag, Tooltip, Button, Dropdown, Menu, Select, message } from 'antd';
import { SeriesIcon } from '../ActionIcons';
import { RightCircleOutlined } from '@ant-design/icons';
import { hasPermission, getMlcTooltip, getUserDetails } from '../../utils/helper';
import { isCancelledStudy } from '../../utils/tableActionHandlers';
import { StudyDateTime } from '../StudyDateTime';
import { Permissions, getPacsStatusLabel } from '../../constants/fieldLabels';
import { ReReportedBadge } from '../ReReportedBadge';
// Shared across every table's Status column — never fork a local copy.
import { getStatusBadgeStyle as getStatusStyle } from '../../utils/statusBadgeStyle';

/**
 * Two-line clamp for text cells — wrap once, then ellipsis, instead of the old
 * single-line truncation. Shared by the simple text cell renderers below.
 */
const TEXT_CLAMP_2: React.CSSProperties = {
  display: '-webkit-box',
  WebkitLineClamp: 2,
  WebkitBoxOrient: 'vertical',
  overflow: 'hidden',
  whiteSpace: 'normal',
  wordBreak: 'break-word',
  width: '100%',
  lineHeight: 1.2,
};

/**
 * Status Cell Renderer
 * Displays status as a colored tag with re-reported indicator
 */
export const StatusCellRenderer: React.FC<ICellRendererParams> = (params) => {
  const record = params.data;
  const status = params.data?.order_workflow?.ow_pacs_status || '';
  const isReReported = record?.order_workflow?.ow_re_reported;
  const onViewReport = params.context?.onViewReport;

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();

    if (status === 'SIGNEDOFF' && onViewReport) {
      onViewReport(record);
    }
  };

  if (!status) return <span>-</span>;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
      <Tag
        style={{
          ...getStatusStyle(status),
          cursor: status === 'SIGNEDOFF' ? 'pointer' : 'default',
        }}
        onClick={handleClick}
      >
        {getPacsStatusLabel(status)}
      </Tag>
      {isReReported && <ReReportedBadge />}
    </div>
  );
};

/**
 * Modality Cell Renderer
 * Displays modality in a styled badge
 */
export const ModalityCellRenderer: React.FC<ICellRendererParams> = (params) => {
  const modality = params.data?.pacs_order?.po_modality || '';

  if (!modality) return <span>-</span>;

  return (
    <span
      title={modality}
      style={{
        background: 'var(--pacs-modality-bg, #F5F0FA)',
        border: '1px solid var(--pacs-modality-border, #E0D8EA)',
        height: '24px',
        borderRadius: '4px',
        padding: '2px 8px',
        display: 'inline-block',
        lineHeight: '20px',
        fontSize: '13px',
        fontWeight: 500,
        color: 'var(--pacs-modality-fg, #4a4a4a)',
        // Grid-wide wrap CSS (word-break: break-word) splits multi-modality
        // csv values like "CT,PT" (T002 item groups) at the comma — keep the
        // badge on one line; anything longer clips with the title tooltip.
        whiteSpace: 'nowrap',
      }}
    >
      {modality}
    </span>
  );
};

/**
 * Patient Name Cell Renderer
 * Clickable button that opens report editor, with emergency indicator
 * Accesses callbacks from AG Grid context (passed via context prop)
 */
export const PatientNameCellRenderer: React.FC<ICellRendererParams> = (params) => {
  const record = params.data;
  const patientName = record?.pacs_order?.patient?.pat_name || '';
  // Emergency cases are now signalled by a reddish row tint (see getRowClass),
  // not an inline icon, so emergency no longer affects this cell's layout.
  const isMlc = record?.pacs_order?.po_is_mlc === true;
  const currentlyBlocked = record?.order_workflow?.ow_reporting_blocked === true;
  const studyUID = record?.ps_study_uid;

  // Access callbacks from AG Grid context
  const onOpenReport = params.context?.onOpenReport;
  const onOpenViewer = params.context?.onOpenViewer;
  const onOpenRadiant = params.context?.onOpenRadiant;
  const enableRadiantViewer = params.context?.enableRadiantViewer;
  const onShowPatientOrders = params.context?.onShowPatientOrders;
  const onShowSelectSeries = params.context?.handleSelectSeries;

  // Technicians don't report — for them the patient-name click opens the viewer
  // instead of the report editor (T009). Everyone else opens reporting as before.
  const isTechnician = getUserDetails()?.user_type === 'technician';
  // A CANCELLED study can never be opened for REPORTING. Technicians are exempt:
  // their name-click opens the viewer, and a cancelled order can still carry images
  // the technician needs to look at — so the block applies to reporters only.
  const isCancelled = isCancelledStudy(record);
  const reportBlockedByCancel = !isTechnician && isCancelled;

  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isTechnician) {
      if (onOpenViewer) onOpenViewer(record);
      return;
    }
    if (isCancelled) {
      message.info('This study is Cancelled and cannot be reported');
      return;
    }
    if (!currentlyBlocked && onOpenReport) {
      onOpenReport(record);
    }
  };

  const handleRadiantClick = (e: any) => {
    e.domEvent?.stopPropagation();
    if (studyUID && onOpenRadiant) {
      onOpenRadiant(studyUID);
    }
  };

  const nameButton = (
    <Button
      type={reportBlockedByCancel ? 'text' : 'link'}
      onClick={handleClick}
      style={{
        padding: 0,
        height: 'auto',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        maxWidth: '100%',
        flex: 1,
        minWidth: 0,
        cursor: reportBlockedByCancel ? 'default' : undefined,
      }}
      disabled={!isTechnician && currentlyBlocked}
    >
      <Tooltip title={patientName}>
        <span
          style={{
            ...TEXT_CLAMP_2,
            // The span is width:100% inside an antd Button, whose `.ant-btn`
            // rule sets text-align:center directly on the button — that beats the
            // text-align:left the cell inherits from an ancestor, so short names
            // render centered. Force left alignment on the span itself.
            textAlign: 'left',
            maxWidth: isMlc ? 'calc(100% - 34px)' : '100%',
            color: (!isTechnician && currentlyBlocked)
              ? 'var(--pacs-cell-muted, #b0b0b0)'
              : 'var(--pacs-link-fg, #2563EB)',
            // Patient name is the primary identifier in the grid — bold so the
            // eye lands on it before the surrounding metadata columns.
            fontWeight: 700,
          }}
        >
          {patientName}
        </span>
      </Tooltip>
      {isMlc && (
        <Tooltip title={getMlcTooltip(record?.pacs_order)}>
          <span
            style={{
              marginLeft: '6px',
              display: 'inline-flex',
              alignItems: 'center',
              height: '15px',
              padding: '0 2px',
              fontSize: '9px',
              fontWeight: 700,
              lineHeight: '15px',
              letterSpacing: '0',
              color: 'var(--pacs-mlc-fg, #fff)',
              background: 'var(--pacs-mlc-bg, #475569)',
              borderRadius: '3px',
            }}
          >
            M
          </span>
        </Tooltip>
      )}
    </Button>
  );
  const ordersButton = (onShowPatientOrders && record?.has_prior_orders) ? (
    <Tooltip title="View Prior Orders">
      <Button
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          onShowPatientOrders(record);
        }}
        style={{
          flexShrink: 0,
          borderRadius: '4px',
          boxShadow: 'none',
        }}
      >
        <RightCircleOutlined/>
      </Button>
    </Tooltip>
  ) : null;

  const seriesButton = (onShowSelectSeries && studyUID) ? (
    <Tooltip title="Select Series">
      <Button
        type="text"
        size="small"
        onClick={e => { e.stopPropagation(); onShowSelectSeries(record); }}
        style={{
          flexShrink: 0,
          background: 'transparent',
          border: 'none',
          boxShadow: 'none',
          padding: 0,
          marginRight: '6px',
        }}
      >
        <SeriesIcon />
      </Button>
    </Tooltip>
  ) : null;

  const fullContent = (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-start', width: '100%', overflow: 'hidden' }}>
      {seriesButton}
      {nameButton}
      {ordersButton}
    </div>
  );

  // Right-click context menu: always offer "Copy Study UID"; the Radiant Viewer
  // entry is added only when Radiant is enabled. fullContent is a full-width div,
  // so the entire cell is the trigger.
  const contextMenu = (
    <Menu>
      <Menu.Item
        key="copy-study-uid"
        disabled={!studyUID}
        onClick={(e) => {
          e.domEvent.stopPropagation();
          if (!studyUID) {
            return;
          }
          navigator.clipboard
            .writeText(studyUID)
            .then(() => message.success('Study UID copied'))
            .catch(() => message.error('Failed to copy Study UID'));
        }}
      >
        Copy Study UID
      </Menu.Item>
      {enableRadiantViewer && onOpenRadiant && studyUID && (
        <Menu.Item key="radiant" onClick={handleRadiantClick}>
          Open in Radiant Viewer
        </Menu.Item>
      )}
    </Menu>
  );

  return (
    <Dropdown overlay={contextMenu} trigger={['contextMenu']}>
      {fullContent}
    </Dropdown>
  );
};

/**
 * Tooltip Text Cell Renderer
 * Wraps text content with ellipsis and tooltip
 */
interface TooltipTextRendererProps extends ICellRendererParams {
  valueGetter?: (data: any) => string;
}

export const TooltipTextCellRenderer: React.FC<TooltipTextRendererProps> = (params) => {
  let value = params.value;

  // If custom valueGetter provided, use it
  if (params.valueGetter) {
    value = params.valueGetter(params.data);
  }

  if (!value) return <span style={{ color: 'var(--pacs-cell-muted, #b0b0b0)', textAlign: 'center', width: '100%', display: 'block' }}>-</span>;

  return (
    <Tooltip title={value}>
      <div
        style={TEXT_CLAMP_2}
      >
        {value}
      </div>
    </Tooltip>
  );
};

/**
 * Patient ID Cell Renderer
 */
export const PatientIdCellRenderer: React.FC<ICellRendererParams> = (params) => {
  const patId = params.data?.pacs_order?.patient?.pat_pin || '';

  if (!patId) return <span>-</span>;

  return (
    <Tooltip title={patId}>
      <div
        style={TEXT_CLAMP_2}
      >
        {patId}
      </div>
    </Tooltip>
  );
};

/**
 * Body Part Cell Renderer
 */
export const BodyPartCellRenderer: React.FC<ICellRendererParams> = (params) => {
  const rawBodyPart = params.data?.pacs_order?.po_body_part || '';
  // When body part couldn't be extracted, dcm4chee stores '*'. Fall back to the
  // study description in that case instead of showing a bare '*'.
  const bodyPart =
    !rawBodyPart || rawBodyPart === '*'
      ? params.data?.pacs_order?.po_diag_desc || ''
      : rawBodyPart;

  if (!bodyPart) return <span>-</span>;

  return (
    <Tooltip title={bodyPart}>
      <div
        style={TEXT_CLAMP_2}
      >
        {bodyPart}
      </div>
    </Tooltip>
  );
};

/**
 * Study Description Cell Renderer
 */
export const StudyDescCellRenderer: React.FC<ICellRendererParams> = (params) => {
  const studyDesc = params.data?.pacs_order?.po_diag_desc || '';
  const priority = params.data?.pacs_order?.po_priority || '';

  if (!studyDesc) return <span>-</span>;

  const tooltipText = priority ? `${studyDesc} (Priority: ${priority})` : studyDesc;

  return (
    <Tooltip title={tooltipText}>
      <div
        style={TEXT_CLAMP_2}
      >
        {studyDesc}
      </div>
    </Tooltip>
  );
};

/**
 * Series/Image Count Cell Renderer
 */
export const SeriesCountCellRenderer: React.FC<ICellRendererParams> = (params) => {
  const seriesCount = params.data?.ps_series_count || 0;
  const imageCount = params.data?.ps_image_count || 0;
  const isIngesting = !params.data?.is_study_ingestion_complete;

  const tooltipText = isIngesting
    ? 'Images are still being received from modality'
    : `Series: ${seriesCount}, Images: ${imageCount}`;

  return (
    <Tooltip title={tooltipText}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: 6,
          width: '100%',
        }}
      >
        <span>
          {seriesCount} / {imageCount}
        </span>
      </div>
    </Tooltip>
  );
};

/**
 * Accession Number Cell Renderer
 */
export const AccessionNumberCellRenderer: React.FC<ICellRendererParams> = (params) => {
  const accNo = params.data?.pacs_order?.po_acc_no || '';

  if (!accNo) return <span>-</span>;

  return (
    <Tooltip title={accNo}>
      <div
        style={TEXT_CLAMP_2}
      >
        {accNo}
      </div>
    </Tooltip>
  );
};

/**
 * Referring Doctor Cell Renderer
 */
export const RefDoctorCellRenderer: React.FC<ICellRendererParams> = (params) => {
  const refDoc = params.data?.pacs_order?.po_ref_doc || '';

  return (
    <Tooltip title={refDoc || 'No referring doctor'}>
      <div
        style={{
          ...TEXT_CLAMP_2,
          textAlign: refDoc ? 'left' : 'center',
          color: refDoc ? 'var(--pacs-cell-fg, #4a4a4a)' : 'var(--pacs-cell-muted, #b0b0b0)',
        }}
      >
        {refDoc || '-'}
      </div>
    </Tooltip>
  );
};

/**
 * Date/Time Cell Renderer
 */
export const DateTimeCellRenderer: React.FC<ICellRendererParams> = (params) => {
  return <StudyDateTime value={params.value} />;
};

/**
 * Reported By Cell Renderer
 */
export const ReportedByCellRenderer: React.FC<ICellRendererParams> = (params) => {
  const userName = params.data?.reportedBy || '';

  if (!userName) return <span>-</span>;

  return (
    <Tooltip title={userName}>
      <div
        style={TEXT_CLAMP_2}
      >
        {userName}
      </div>
    </Tooltip>
  );
};

/**
 * Signed By Cell Renderer
 */
export const SignedByCellRenderer: React.FC<ICellRendererParams> = (params) => {
  const userName = params.data?.signedBy || '';

  if (!userName) return <span>-</span>;

  return (
    <Tooltip title={userName}>
      <div
        style={TEXT_CLAMP_2}
      >
        {userName}
      </div>
    </Tooltip>
  );
};

/**
 * Assigned Radiologist Cell Renderer
 * Displays a dropdown for users with ASSIGN_CASES permission, plain text otherwise
 * Accesses userList and onRadiologistAssignment from AG Grid context
 */
export const AssignedRadiologistCellRenderer: React.FC<ICellRendererParams> = (params) => {
  const record = params.data;
  const assignedRadId = record?.order_workflow?.ow_assigned_rad_id;
  // const assignedRadName = record?.order_workflow?.ow_assigned_rad_name;
  // Radiologists never get the assignment dropdown, even if granted ASSIGN_CASES —
  // it is reserved for HOD/coordinators.
  const isRadiologist = getUserDetails()?.user_type === 'radiologist';
  const canAssign = hasPermission(Permissions.ASSIGN_CASES) && !isRadiologist;

  // Access data from AG Grid context
  const userList = params.context?.userList || [];
  const onRadiologistAssignment = params.context?.onRadiologistAssignment;
  const pacs_status = record?.order_workflow?.ow_pacs_status;
  const isSignedOff = pacs_status === 'SIGNEDOFF';
  if (canAssign) {
    // Assignable reporters: radiologists AND HODs (an HOD can assign a study to
    // another HOD or to themselves for review). HODs are tagged " — HOD" so they
    // are distinguishable from radiologists in the list.
    const radiologistOptions = userList
      ?.filter((u: any) => u.user_type === 'radiologist' || u.user_type === 'hod')
      .map((user: any) => ({
        label: `${user.user_fullname} (${user.username})${user.user_type === 'hod' ? ' — HOD' : ''}`,
        value: user.username,
      })) || [];

  // For SIGNEDOFF status, show Select disabled
      return (
        <Select
          value={assignedRadId || undefined}
          placeholder="Unassigned"
          allowClear={!isSignedOff}
          disabled={isSignedOff}
          showSearch={!isSignedOff}
          options={radiologistOptions}
          optionFilterProp="label"
          onChange={(value) => {
            if (onRadiologistAssignment) {
              onRadiologistAssignment(record, value, params.rowIndex);
            }
          }}
          style={{
            width: '100%',
            minWidth: '150px',
          }}
        />
      );
  }

  // Show plain text for users without permission
  return (
    <Tooltip title={assignedRadId || 'Unassigned'}>
      <div
        style={{
          ...TEXT_CLAMP_2,
          color: assignedRadId ? 'var(--pacs-cell-fg, #4a4a4a)' : 'var(--pacs-cell-muted, #b0b0b0)',
        }}
      >
        {assignedRadId || '-'}
      </div>
    </Tooltip>
  );
};

/**
 * Assigned Technician Cell Renderer
 * Read-only: the study list only displays who the study is assigned to. Actual
 * assignment happens on the Orders page (its own technician dropdown column).
 * The stored value is a username; it is resolved to the technician's full name
 * for display when that user is present in the loaded user list.
 */
export const AssignedTechnicianCellRenderer: React.FC<ICellRendererParams> = (params) => {
  const record = params.data;
  const assignedTechnician = record?.order_workflow?.ow_assigned_technician;

  const userList: any[] = params.context?.userList || [];
  const assignedUser = assignedTechnician
    ? userList.find(u => u?.username === assignedTechnician)
    : undefined;
  const displayName = assignedTechnician
    ? assignedUser?.user_fullname || assignedTechnician
    : '';

  return (
    <Tooltip title={displayName || 'Unassigned'}>
      <div
        style={{
          ...TEXT_CLAMP_2,
          color: displayName ? 'var(--pacs-cell-fg, #4a4a4a)' : 'var(--pacs-cell-muted, #b0b0b0)',
        }}
      >
        {displayName || '-'}
      </div>
    </Tooltip>
  );
};
